import React from 'react';
import Icon from '../../../components/AppIcon';

const ComplianceTracker = ({ country, serviceAvailability, loading, language }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!country) {
    return (
      <div className="text-center py-12">
        <Icon name="Globe" size={48} className="mx-auto mb-4 text-muted-foreground" />
        <p className="text-muted-foreground">Sélectionnez un pays pour voir la conformité</p>
      </div>
    );
  }

  const getComplianceStatusColor = (status) => {
    switch (status) {
      case 'compliant':
        return 'text-green-500 bg-green-500/10';
      case 'pending':
        return 'text-yellow-500 bg-yellow-500/10';
      case 'non_compliant':
        return 'text-red-500 bg-red-500/10';
      case 'under_review':
        return 'text-blue-500 bg-blue-500/10';
      default:
        return 'text-gray-500 bg-gray-500/10';
    }
  };

  const getComplianceStatusLabel = (status) => {
    switch (status) {
      case 'compliant':
        return 'Conforme';
      case 'pending':
        return 'En attente';
      case 'non_compliant':
        return 'Non conforme';
      case 'under_review':
        return 'En révision';
      default:
        return 'Inconnu';
    }
  };

  const compliantServices = serviceAvailability?.filter(s => s?.complianceStatus === 'compliant')?.length || 0;
  const pendingServices = serviceAvailability?.filter(s => s?.complianceStatus === 'pending')?.length || 0;
  const nonCompliantServices = serviceAvailability?.filter(s => s?.complianceStatus === 'non_compliant')?.length || 0;
  const totalServices = serviceAvailability?.length || 0;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Suivi de Conformité - {country?.countryName}
        </h3>
        <p className="text-sm text-muted-foreground">
          Statut de conformité réglementaire pour tous les services
        </p>
      </div>

      {/* Compliance Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Total Services</p>
          <p className="text-2xl font-bold text-foreground">{totalServices}</p>
        </div>
        <div className="bg-card border border-green-500/20 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Conformes</p>
          <p className="text-2xl font-bold text-green-500">{compliantServices}</p>
        </div>
        <div className="bg-card border border-yellow-500/20 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">En Attente</p>
          <p className="text-2xl font-bold text-yellow-500">{pendingServices}</p>
        </div>
        <div className="bg-card border border-red-500/20 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Non Conformes</p>
          <p className="text-2xl font-bold text-red-500">{nonCompliantServices}</p>
        </div>
      </div>

      {/* Services List */}
      <div className="space-y-3">
        {serviceAvailability?.map((service) => (
          <div
            key={service?.id}
            className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="font-semibold text-foreground">{service?.serviceName}</h4>
                  <span className="text-xs font-mono text-muted-foreground">
                    {service?.serviceCode}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Catégorie:</span>
                    <span className="font-medium text-foreground">{service?.category}</span>
                  </div>
                  {service?.certificationRequired && (
                    <div className="flex items-center gap-2">
                      <Icon name="Shield" size={16} className="text-blue-500" />
                      <span className="text-blue-500 text-xs font-medium">
                        Certification requise
                      </span>
                    </div>
                  )}
                </div>
                {service?.regulatoryNotes && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {service?.regulatoryNotes}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getComplianceStatusColor(service?.complianceStatus)}`}>
                  {getComplianceStatusLabel(service?.complianceStatus)}
                </span>
                {service?.isAvailable ? (
                  <span className="flex items-center gap-1 text-xs text-green-500">
                    <Icon name="CheckCircle" size={14} />
                    Disponible
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <Icon name="XCircle" size={14} />
                    Non disponible
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {serviceAvailability?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Aucun service configuré pour ce pays</p>
        </div>
      )}
    </div>
  );
};

export default ComplianceTracker;